import z from "zod";
import { patientTypeSchema } from "@medoma/models/patients";
import {
  categorySchema,
  measurementSchema,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { medicalCompetenceSchema } from "@medoma/models/shifts";
import {
  customDurationUnitSchema,
  durationOptionSchema,
} from "./TemplateFormFields/TemplateDuration/durationModels";
import {
  activityFrequencySchema,
  customRecurrenceIntervalSchema,
  customRecurrenceIntervalUnitSchema,
  recurringOptionSchema,
} from "./TemplateFormFields/TemplateScheduling/schedulingModels";
import { weekdaySchema } from "@/api/Activities";
import { recurrencesPerDayOptionSchema } from "./TemplateFormFields/TemplateScheduling/timeSlotsUtils";

const templateTitleSchema = z.object({
  title: z.string(),
});

const templateActivityCategorySchema = z.object({
  category: categorySchema,
});

export const templatePatientTypeSchema = z.object({
  patientType: patientTypeSchema,
});

const templateWithoutPatientSchema = z.object({
  withoutPatient: z.literal(true),
});

const templateMeasurementsSchema = z.object({
  measurements: z.array(measurementSchema).nullish(),
});

export const templateDescriptionSchema = z.object({
  description: z.string().nullish(),
});

const templateInvoicingSchema = z.object({
  procedureCodes: z.array(z.string()).nullish(),
  productCodes: z.array(z.string()).nullish(),
});

const templateCompetenceSchema = z.object({
  requiredCompetences: z.array(medicalCompetenceSchema).nullish(),
});

const templateCustomDurationSchema = z.object({
  customDuration: z.string().nullish(),
  customDurationUnit: customDurationUnitSchema.nullish(),
});

const templateDurationSchema = z.object({
  durationOption: durationOptionSchema.nullish(),
});

const templateFrequencySchema = z.object({
  frequency: activityFrequencySchema.nullish(),
});

const templateRecurrenceSchema = z.object({
  recurrence: recurringOptionSchema.nullish(),
});

const templateWeekdaysSchema = z.object({
  weekdays: z.array(weekdaySchema).nullish(),
});

const templateCustomRecurringIntervalsSchema = z.object({
  customRecurrenceInterval: customRecurrenceIntervalSchema.nullish(),
  customRecurrenceIntervalUnit: customRecurrenceIntervalUnitSchema.nullish(),
});

const templateTimeSlotsSchema = z.object({
  timeslots: z.array(z.object({ time: z.string() })).nullish(),
});

const templateTimeSensitivitySchema = z.object({
  timeSensitivity: z.string().nullish(),
});

const templateTimeCategorySchema = z.object({
  timeCategory: timeOfDaySchema.nullish(),
});

const templateRecurrencesPerDaySchema = z.object({
  recurrencesPerDay: recurrencesPerDayOptionSchema.nullish(),
});

const templateTimeSlotsPickerSchema = templateTimeSlotsSchema
  .extend(templateTimeSensitivitySchema.shape)
  .extend(templateTimeCategorySchema.shape)
  .extend(templateRecurrencesPerDaySchema.shape);

export const templateScheduleSchema = templateFrequencySchema
  .extend(templateRecurrenceSchema.shape)
  .extend(templateWeekdaysSchema.shape)
  .extend(templateCustomRecurringIntervalsSchema.shape)
  .extend(templateTimeSlotsPickerSchema.shape);

const templateAdditionalFieldsSchema = z.object({
  doubleStaffing: z.boolean().nullish(),
  hidden: z.boolean().nullish(),
});

export const templateFormBaseSchema = z
  .object()
  .extend(templateTitleSchema.shape)
  .extend(templateActivityCategorySchema.shape)
  .extend(templateMeasurementsSchema.shape)
  .extend(templateDescriptionSchema.shape)
  .extend(templateInvoicingSchema.shape)
  .extend(templateCompetenceSchema.shape)
  .extend(templateCustomDurationSchema.shape)
  .extend(templateDurationSchema.shape)
  .extend(templateScheduleSchema.shape)
  .extend(templateAdditionalFieldsSchema.shape);

export const templateFormWithPatientSchema = templateFormBaseSchema.extend(
  templatePatientTypeSchema.shape,
);

export const templateFormWithoutPatientSchema = templateFormBaseSchema.extend(
  templateWithoutPatientSchema.shape,
);

const _templateFormSchema = z.union([
  templateFormWithPatientSchema,
  templateFormWithoutPatientSchema,
]);

export type TemplateTitleInputFields = z.infer<typeof templateTitleSchema>;
export type TemplateActivityCategoryPickerFields = z.infer<
  typeof templateActivityCategorySchema
>;
export type TemplatePatientTypePickerFields = z.infer<
  typeof templatePatientTypeSchema
>;
export type TemplateMeasurementsPickerFields = z.infer<
  typeof templateMeasurementsSchema
>;
export type TemplateDescriptionInputFields = z.infer<
  typeof templateDescriptionSchema
>;
export type TemplateInvoicingCodesPickerFields = z.infer<
  typeof templateInvoicingSchema
>;
export type TemplateCompetencePickerFields = z.infer<
  typeof templateCompetenceSchema
>;
export type TemplateCustomDurationFields = z.infer<
  typeof templateCustomDurationSchema
>;
export type TemplateDurationFields = z.infer<typeof templateDurationSchema>;
export type TemplateFrequencyPickerFields = z.infer<
  typeof templateFrequencySchema
>;
export type TemplateRecurrencePickerFields = z.infer<
  typeof templateRecurrenceSchema
>;
export type TemplateWeekdaysPickerFields = z.infer<
  typeof templateWeekdaysSchema
>;
export type TemplateCustomRecurringIntervalFields = z.infer<
  typeof templateCustomRecurringIntervalsSchema
>;
export type TemplateTimeSlotsFields = z.infer<typeof templateTimeSlotsSchema>;
export type TemplateTimeSensitivityFields = z.infer<
  typeof templateTimeSensitivitySchema
>;
export type TemplateTimeSlotsPickerFields = z.infer<
  typeof templateTimeSlotsPickerSchema
>;
export type TemplateSchedulingFields = z.infer<typeof templateScheduleSchema>;
export type TemplateFormFields = z.infer<typeof _templateFormSchema>;
